import {SiteId} from "@app/shared/services/api/access-sites.interface";
import {ImpactId, PriorityId} from '@shared/api/model/type-ids';
import {MetadataId, MetadataTypeId} from "@app/graphql-typings/graphql";
import {TemplateCategoryId} from "@app/layout/rules/template-categories/models/template-category";

export class TemplateProperties {
  rule_name: string;
  rule_solution: string;
  rule_description: string;
}

export enum RuleCategoryId {
  CONTROL = 1,
  MECHANICAL = 2,
  SENSOR = 3
}

export class RuleCategory {
  category_id: RuleCategoryId;
  name: string;
}

export class ImpactIdAndName {
  impact_id: ImpactId;
  name: string;
}

export class Impact extends ImpactIdAndName {
  customer_id: number;
  shared: boolean;
}

export const COMFORT_IMPACT_ID = 'a3554cff-e909-4810-be85-5318f07461fc';
export const ENERGY_IMPACT_ID = '4e8da0a2-f0d7-4d26-89cc-42bdb48a3aa3';
export const RELIABILITY_IMPACT_ID = 'c2e1a282-0eb0-4ec9-b25b-924d7f7aebcd';
export const SAFETY_IMPACT_ID = '813c161e-5971-489e-9b88-f3d0af656e5b';
export const WATER_IMPACT_ID = '49199edf-c534-49f1-8ec7-d35f47eb0040';
export const OTHER_IMPACT_ID = '3f8d4f56-f855-49c0-a1a8-b128197754fc';

export const IMPACT_NAMES_BY_ID = new Map<ImpactId, string>([
  [COMFORT_IMPACT_ID, 'Comfort'],
  [ENERGY_IMPACT_ID, 'Energy'],
  [RELIABILITY_IMPACT_ID, 'Reliability'],
  [SAFETY_IMPACT_ID, 'Safety'],
  [WATER_IMPACT_ID, 'Water'],
  [OTHER_IMPACT_ID, 'Other'],
]);


export const RULE_CATEGORIES: RuleCategory[] = [
  {category_id: RuleCategoryId.CONTROL, name: 'Control'},
  {category_id: RuleCategoryId.MECHANICAL, name: 'Mechanical'},
  {category_id: RuleCategoryId.SENSOR, name: 'Sensor'}];

export enum GateType {
  AND = 'AND',
  OR = 'OR',
  GENERAL = 'GENERAL'
}

export type Position = number[];

export interface TemplateNode {
  position: Position;
}

export interface Priority {
  priority_id: PriorityId;
  name: string;
  rank: number;
}

export abstract class NamedTemplateNode implements TemplateNode {
  name: string;
  position: Position;
}

export class Sensor extends NamedTemplateNode {
  sensor_id?: number;
  metadata_id: number;
}

export class TemplateFunction extends NamedTemplateNode {
  function_id: number;
  formula: string;
  threshold: number;
}

export interface AbstractGate extends TemplateNode {
  type: GateType;
  source_nodes: string[];
  trigger_states: string[][];
}

export class Gate extends NamedTemplateNode implements AbstractGate {
  gate_id?: number;
  type: GateType;
  source_nodes: string[];
  trigger_states: string[][];
}

export class FaultGate implements AbstractGate, TemplateNode {
  source_nodes: string[];
  trigger_states: string[][];
  position: Position;

  type = GateType.AND;
}

export class TemplateEdge {
  source_node: string;
  target_node: string;
  state_trigger?: {
    from: string;
    to: string;
  };
}

export type TemplateId = string;
export type TemplateDraftId = string;

export class Template {
  template_id?: TemplateId;
  template_name: string;
  properties: TemplateProperties;
  category: RuleCategoryId[];
  sensors: Sensor[];
  functions: TemplateFunction[];
  gates: Gate[];
  relations: TemplateEdge[];
  fault: FaultGate;
  is_draft?: boolean;
  draft_id?: TemplateDraftId;
  draft_awaiting_approval?: boolean;
  is_active?: boolean;
  created_by?: string;
  created_at?: Date;
  last_modified_at?: Date;
  customer_id: number;
  shared: boolean;
  target_running_load: number;
  no_calculation: boolean;
  primary_metadata_type_ids: number[];
  impact_ids?: ImpactId[];
  priority_id: PriorityId;
  template_category_id?: TemplateCategoryId;

  static createEmptyTemplate(customProperties?: any): Template {
    const template = new Template();
    template.properties = {
      rule_name: null,
      rule_description: null,
      rule_solution: null,
    };
    template.category = [];
    template.impact_ids = [];
    template.priority_id = null;
    template.sensors = [];
    template.functions = [];
    template.gates = [];
    template.relations = [];
    template.fault = {
      type: GateType.AND,
      source_nodes: [],
      trigger_states: [],
      position: [0, 0]
    };
    template.no_calculation = true;

    if (customProperties) {
      Object.assign(template, customProperties);
    }

    return template;
  }

  public isEmpty(): boolean {
    return this.sensors.length === 0 && this.functions.length === 0 && this.gates.length === 0 && this.relations.length === 0;
  }

  public isDraft(): boolean {
    return !!this.is_draft;
  }

  public hasDraft(): boolean {
    return !!this.draft_id;
  }

  public isDraftSubmittedForApproval(): boolean {
    return this.hasDraft() && this.draft_awaiting_approval;
  }
}

export interface TemplateDraftSearchTerms {
  template_name?: string;
  rule_name?: string;
  draft_awaiting_approval?: boolean;
  primary_metadata_type_ids?: MetadataTypeId[];
  metadata_ids?: MetadataId[];
  match_metadata_ids_using_and?: boolean;
  template_category_id?: TemplateCategoryId;
  template_category_ids?: TemplateCategoryId[];
}

export interface TemplateSearchTerms extends TemplateDraftSearchTerms {
  site_ids?: SiteId[];
  shared?: boolean;
}
